import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useOnboarding } from '../onboarding/OnboardingProvider';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import { SignOut, List, LockKey, Compass } from '@phosphor-icons/react';
import NotificationsBell from '../notifications/NotificationsBell';

/* TopNav exposes a hamburger that triggers the mobile sidebar drawer.
   The hamburger is hidden on md+ since the sidebar is permanently visible there. */
export default function TopNav({ onMobileMenu }) {
  const { user, logout, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const onb = useOnboarding();

  const enabledModules = new Set(user?.enabled_modules || []);
  const canSeeNotifications = isSuperAdmin || enabledModules.has('notifications');

  return (
    <header
      className="h-16 bg-[#FFFFFF] border-b border-[#EDE6D8] flex items-center justify-between px-4 md:px-6"
      data-testid="top-nav"
    >
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onMobileMenu}
          data-testid="topnav-hamburger"
          aria-label="Open menu"
          className="md:hidden p-2 rounded-md text-[#0A2540] hover:bg-[#FCFAF6]"
        >
          <List size={22} weight="bold" />
        </button>
      </div>

      <div className="flex items-center gap-3">
        {canSeeNotifications && <NotificationsBell />}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="w-8 h-8 bg-[#0A2540] rounded-full flex items-center justify-center text-white text-sm font-medium hover:ring-2 hover:ring-[#0A2540]/30 focus:outline-none focus:ring-2 focus:ring-[#0A2540]/40 transition"
              data-testid="user-avatar"
              aria-label="Account menu"
            >
              {user?.fullname?.charAt(0)?.toUpperCase() || 'U'}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56" data-testid="user-menu">
            <DropdownMenuLabel className="flex flex-col gap-0.5">
              <span className="text-sm font-semibold text-[#1C1917]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                {user?.fullname || 'User'}
              </span>
              <span className="text-[10px] uppercase tracking-widest text-[#78716C]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {user?.role?.replace('_', ' ') || 'member'}
              </span>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => navigate('/change-password')}
              data-testid="change-password-menu-item"
              className="cursor-pointer text-[#0A2540] focus:bg-[#0A2540]/5"
            >
              <LockKey size={14} className="mr-2" />
              Change password
            </DropdownMenuItem>
            {onb?.canShowSetupGuide && (
              <DropdownMenuItem
                onClick={() => onb.launch()}
                data-testid="setup-guide-menu-item"
                className="cursor-pointer text-[#0A2540] focus:bg-[#0A2540]/5"
              >
                <Compass size={14} className="mr-2" />
                Setup Guide
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={logout}
              data-testid="logout-menu-item"
              className="text-[#EF4444] focus:text-[#EF4444] focus:bg-[#EF4444]/10 cursor-pointer"
            >
              <SignOut size={14} className="mr-2" />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
